import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Anchor } from '@mantine/core';
import { clsx } from 'clsx';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';

import { trackingHistorySectionMessages } from './tracking-history-section-messages';
import styles from './TrackingHistorySection.module.css';
import { parseFreightStatus } from '../../parse-freight-status';
import { DetailSection } from '../detail-section/DetailSection';

import type { LtlShipment } from '../../../../types/ltl';
import type { FreightTrackingHistory } from '../../freight-details-types';
import type { MessageDescriptor } from 'react-intl';

type TrackingHistorySectionProps = {
  shipment: LtlShipment;
  trackingHistory?: FreightTrackingHistory | null;
  onTrack?: () => void;
};

const STEP_LABELS: MessageDescriptor[] = [
  trackingHistorySectionMessages.stepPendingPickup,
  trackingHistorySectionMessages.stepInTransit,
  trackingHistorySectionMessages.stepDelivered,
];

/**
 * Derives the active stepper index from the shipment status.
 * `Booked`/`Quoted`/unknown → 0 (Pending Pickup), `InTransit` → 1, `Delivered` → 2.
 * `Cancelled`/`Exception` leave step 0 active — the simpler defensible default,
 * since the three-step happy path has no terminal/error step to map them onto.
 */
const activeStepIndex = (status: string): number => {
  switch (parseFreightStatus(status)) {
    case 'InTransit':
      return 1;
    case 'Delivered':
      return 2;
    default:
      return 0;
  }
};

/** Safely reads a string field off an unknown tracking event. */
const readEventField = (event: unknown, field: string): string => {
  if (typeof event !== 'object' || event === null) return '';
  const value = (event as Record<string, unknown>)[field];
  return typeof value === 'string' ? value : '';
};

const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : isoDate;
};

/**
 * Right-column Tracking card (Phase 1, §6.4 / §7.5). Renders a three-step
 * horizontal stepper (Pending Pickup → In Transit → Delivered; "Out for
 * Delivery" omitted per §6.4) driven by `shipment.status`, plus the PRO Number
 * external tracking link. The Updates accordion renders only when a
 * `trackingHistory` prop with non-empty `events` is supplied. Store-agnostic.
 */
export const TrackingHistorySection = ({ shipment, trackingHistory, onTrack }: TrackingHistorySectionProps) => {
  const intl = useIntl();

  const activeIndex = activeStepIndex(shipment.status);
  const proNumber = shipment.proNumber ?? '';
  const trackingUrl = trackingHistory?.trackingUrl ?? undefined;

  // §7.5 Phase 1: events is unknown[] — Phase 2 redux wiring defines the canonical shape.
  const events = trackingHistory?.events ?? [];
  const hasEvents = Array.isArray(events) && events.length > 0;
  const lastUpdated = formatDate(trackingHistory?.lastUpdated ?? '');

  return (
    <DetailSection testId='tracking-history-section' title={intl.formatMessage(trackingHistorySectionMessages.sectionTitle)}>
      <div className={styles.stepper}>
        {STEP_LABELS.map((label, index) => {
          const isCompleted = index < activeIndex;
          const isActive = index === activeIndex;

          return (
            <div className={styles.step} key={label.id ?? index}>
              <div
                className={clsx(styles.dot, isCompleted && styles.dotCompleted, isActive && styles.dotActive)}
                data-testid={`tracking-step-${index}`}
              />
              <span className={clsx(styles.stepLabel, isCompleted && styles.stepLabelCompleted, isActive && styles.stepLabelActive)}>
                {intl.formatMessage(label)}
              </span>
            </div>
          );
        })}
      </div>

      {proNumber !== '' && (
        <div className={styles.proRow}>
          <span className={styles.proLabel}>{intl.formatMessage(trackingHistorySectionMessages.proNumberLabel)}</span>
          <Anchor className={styles.proLink} href={trackingUrl} onClick={() => onTrack?.()} rel='noopener noreferrer' target='_blank'>
            {proNumber}
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </Anchor>
        </div>
      )}

      {hasEvents && (
        <Accordion className={styles.updates} variant='contained'>
          <Accordion.Item value='updates'>
            <Accordion.Control>{intl.formatMessage(trackingHistorySectionMessages.updatesTitle)}</Accordion.Control>
            <Accordion.Panel>
              {events.map((event, index) => {
                const status = readEventField(event, 'status') || readEventField(event, 'description');
                const updatedBy = readEventField(event, 'updatedBy');
                const timestamp = readEventField(event, 'timestamp');
                const meta = [updatedBy, formatDate(timestamp)].filter(Boolean).join(' · ');

                return (
                  <div className={styles.event} key={index}>
                    {status !== '' && <span className={styles.eventStatus}>{status}</span>}
                    {meta !== '' && <span className={styles.eventMeta}>{meta}</span>}
                  </div>
                );
              })}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}

      {lastUpdated !== '' && (
        <span className={styles.lastUpdated}>{intl.formatMessage(trackingHistorySectionMessages.lastUpdated, { date: lastUpdated })}</span>
      )}
    </DetailSection>
  );
};
